import { useEffect, useState } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Mail, ExternalLink, Download, CheckCircle2, Clock, Building2, ShieldCheck, ChevronRight,
} from "lucide-react";

const MailboxSetupPage = () => {
  const [status, setStatus] = useState(null);
  const [guide, setGuide]   = useState(null);
  const [fwd, setFwd]       = useState("");
  const [includeDone, setIncludeDone] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, g] = await Promise.all([
          api.get("/mailbox/status"),
          api.get("/mailbox/setup-guide"),
        ]);
        setStatus(s.data);
        setGuide(g.data);
      } catch (e) {
        console.error("MailboxSetupPage: failed to load setup data", e);
        toast.error("Failed to load mailbox setup data");
      }
    })();
    // Mount-only fetch — IIFE owns its locals.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (fwd)              params.append("forward_to", fwd);
      if (!includeDone)     params.append("include_provisioned", "false");
      const r = await api.get(`/mailbox/forwarding-export.csv?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `echosense-forwarding-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Forwarding CSV downloaded");
    } catch (e) {
      toast.error("CSV download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <DashboardLayout title="Mailbox Setup" subtitle="Real mailbox + email forwarding at echosensecapital.com">
      {/* Status tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <StatTile icon={Mail}          label="Total handles assigned" value={status?.total_with_handle ?? "—"} testid="mailbox-stat-total" />
        <StatTile icon={CheckCircle2}  label="Provisioned"            value={status?.provisioned ?? "—"}       color="emerald" testid="mailbox-stat-provisioned" />
        <StatTile icon={Clock}         label="Pending provisioning"   value={status?.pending ?? "—"}           color="amber"   testid="mailbox-stat-pending" />
      </div>

      {/* Option D — CSV export for forwarding */}
      <Card className="mb-6" data-testid="forwarding-csv-card">
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-2">
            <Download size={16} className="text-blue-600" />
            <h2 className="text-lg font-semibold text-slate-900">Forwarding CSV (Option D — GoDaddy / Cloudflare)</h2>
          </div>
          <p className="text-sm text-slate-600">
            Download a ready-to-import CSV of every auto-generated handle and where it should forward.
            Import into GoDaddy Email Forwarding or Cloudflare Email Routing to get every
            <code className="mx-1 font-mono text-xs bg-slate-100 px-1 rounded">f.last@echosensecapital.com</code>
            address working without per-user setup.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Input
              type="email"
              placeholder="forward all aliases to (optional)"
              value={fwd}
              onChange={(e) => setFwd(e.target.value)}
              className="max-w-xs"
              data-testid="forwarding-target"
            />
            <label className="text-xs text-slate-500 flex items-center gap-2">
              <input type="checkbox" checked={includeDone} onChange={(e) => setIncludeDone(e.target.checked)} />
              Include already-provisioned
            </label>
            <Button onClick={downloadCsv} disabled={downloading} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="forwarding-download">
              <Download size={14} className="mr-1" /> {downloading ? "Generating…" : "Download CSV"}
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Leave the target blank to forward each alias to the user's own real email (the one they registered with).
            Fill it to forward everything to a single inbox you control.
          </p>
        </CardContent>
      </Card>

      {/* Provider cards */}
      <div className="space-y-4">
        {guide?.providers.map((p) => (
          <Card key={p.key} data-testid={`provider-${p.key}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <Building2 size={16} className="text-blue-600" />
                    {p.name}
                  </h3>
                  <p className="text-sm text-slate-500 mt-0.5">{p.summary}</p>
                </div>
                {p.key.startsWith("godaddy") && (
                  <a href={guide.admin_console_links.godaddy} target="_blank" rel="noreferrer noopener">
                    <Button variant="outline" size="sm"><ExternalLink size={12} className="mr-1" /> Open GoDaddy</Button>
                  </a>
                )}
                {p.key.startsWith("google") && (
                  <a href={guide.admin_console_links.google} target="_blank" rel="noreferrer noopener">
                    <Button variant="outline" size="sm"><ExternalLink size={12} className="mr-1" /> Open Google Admin</Button>
                  </a>
                )}
                {p.key.startsWith("microsoft") && (
                  <a href={guide.admin_console_links.microsoft} target="_blank" rel="noreferrer noopener">
                    <Button variant="outline" size="sm"><ExternalLink size={12} className="mr-1" /> Open M365 Admin</Button>
                  </a>
                )}
              </div>
              <ol className="space-y-1.5 mt-3">
                {p.steps.map((step, i) => (
                  <li key={`${p.heading || 'step'}-${i}-${step.slice(0,20)}`} className="flex gap-3 text-sm text-slate-700">
                    <span className="text-[10px] font-mono text-slate-400 mt-1 shrink-0">{(i + 1).toString().padStart(2, "0")}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        ))}
      </div>
    </DashboardLayout>
  );
};

const StatTile = ({ icon: Icon, label, value, color = "blue", testid }) => {
  const colorCls = {
    blue:    "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber:   "bg-amber-50 text-amber-700",
  }[color];
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${colorCls} shrink-0`}>
          <Icon size={18} />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default MailboxSetupPage;
